import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '@/store'
import { Card, Alert } from '@/components/ui'
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew'
import type { SharingListResult, Invitation, SharedUser } from '@/services/api/sharingService'
import { getSharingList, getPendingInvitation, getUnlinkedDeviceList } from '@/services/api/sharingService'
import InviteUserSidebar from '@/components/ui/Sidebar/InviteUserSidebar'
import { FiPlus, FiEdit, FiTrash2, FiCheck, FiUser, FiMail, FiMapPin, FiHash, FiCpu, FiChevronRight } from 'react-icons/fi'
import { useAuth } from '@/hooks/useAuth'
import { apiService } from '@/utils/api'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import EditUserSidebar from '@/components/ui/Sidebar/EditUserSidebar'
import type { SimpleDevice } from '@/types/api'
import SelectSiteSidebar from '@/components/ui/Sidebar/SelectSiteSidebar'
import { getSites, assignDeviceToSite, refreshSites } from '@/services/api/siteService'
import type { Site } from '@/types/api'
import { setLoading } from '@/store/slices/appSlice';

const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="mb-3">
    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
  </div>
)

const UserItem: React.FC<{ user: SharedUser; canManage?: boolean; onEdit?: (u: SharedUser) => void; onDelete?: (u: SharedUser) => void }> = ({ user, canManage = false, onEdit, onDelete }) => {
  const perms = user.permission || { fullAccess: false, readOnly: false, ownerShip: false }
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
      <div className="min-w-0">
        <div className="font-medium truncate dark:text-gray-100">{user.userName || user.inviteEmail || 'Unknown user'}</div>
        {user.inviteEmail && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.inviteEmail}</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {perms.ownerShip && (
          <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">Owner</span>
        )}
        {perms.fullAccess && (
          <span className="px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Full Access</span>
        )}
        {perms.readOnly && (
          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">Read Only</span>
        )}
        {canManage && (
          <div className="flex items-center gap-1 ml-2">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
                aria-label="Edit user"
                onClick={() => onEdit?.(user)}
              >
                <FiEdit className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-red-600 dark:text-red-400"
                aria-label="Delete user"
                onClick={() => onDelete?.(user)}
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const InvitationItem: React.FC<{ inv: Invitation; onAccept?: (inv: Invitation) => void; accepting?: boolean }> = ({ inv, onAccept, accepting = false }) => {
  const initials = (inv.name || inv.email || '?').slice(0, 2).toUpperCase()
  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-9 w-9 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-sm font-semibold select-none">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold truncate">{inv.name || inv.email}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Pending</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center truncate"><FiUser className="mr-1 h-4 w-4" /> Invitee</span>
            <span className="inline-flex items-center min-w-0 truncate"><FiMail className="mr-1 h-4 w-4" /> {inv.email}</span>
            <span className="inline-flex items-center min-w-0 truncate"><FiCpu className="mr-1 h-4 w-4" /> Device ID: {inv.deviceId}</span>
            <span className="inline-flex items-center min-w-0 truncate"><FiMapPin className="mr-1 h-4 w-4" /> {inv.deviceLocation}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 shrink-0"
        onClick={() => onAccept?.(inv)}
        disabled={accepting}
        aria-label="Accept invitation"
      >
        <FiCheck className="h-4 w-4" /> Accept
      </button>
    </div>
  )
}

const UsersSharingPage: React.FC = () => {
  const dispatch = useDispatch();
  const userType = useSelector((state: RootState) => state.auth.user?.userType)
  const appLoading = useSelector((state: RootState) => state.app.loading)
  const initialSelectedDeviceIdFromStore = useSelector((state: RootState) => state.device.selectedDeviceId)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(initialSelectedDeviceIdFromStore || '')
  const [sharing, setSharing] = useState<SharingListResult | null>(null)
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [unlinkedDevices, setUnlinkedDevices] = useState<SimpleDevice[]>([])
  const [error, setError] = useState<string | null>(null)

  const owners = useMemo(() => sharing?.dataList?.[0] || [], [sharing])
  const accepted = useMemo(() => sharing?.dataList?.[1] || [], [sharing])
  const pending = useMemo(() => sharing?.dataList?.[2] || [], [sharing])

  const { currentUser } = useAuth()

  const isOwner = !!(sharing && !sharing.hasError && sharing.owner)

  const hasFullAccess = useMemo(() => {
    if (!sharing || sharing.hasError) return false
    if (sharing.owner) return true
    const acceptedList = sharing.dataList?.[1] || []
    return acceptedList.some(u => {
      return !!u.permission?.fullAccess
    })
  }, [sharing])

  const [inviteOpen, setInviteOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<SharedUser | null>(null)
  const [ownershipConfirmOpen, setOwnershipConfirmOpen] = useState(false)
  const [ownershipWorking, setOwnershipWorking] = useState(false)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState<SharedUser | null>(null)
  const [acceptingId, setAcceptingId] = useState<string | null>(null)
  const [siteSidebarOpen, setSiteSidebarOpen] = useState(false)
  const [sites, setSites] = useState<Site[]>([])
  const [sitesError, setSitesError] = useState<string | null>(null)
  const [selectedUnlinked, setSelectedUnlinked] = useState<SimpleDevice | null>(null)
  const [selectedSite, setSelectedSite] = useState<Site | null>(null)
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false)
  const [assignWorking, setAssignWorking] = useState(false)

  const openSitePickerForDevice = async (device: SimpleDevice) => {
    setSelectedUnlinked(device)
    setSiteSidebarOpen(true)
    dispatch(setLoading(true))
    setSitesError(null)
    try {
      const resp = await getSites()
      setSites(resp.sites || [])
    } catch (e: any) {
      console.error('Failed to fetch sites:', e)
      setSitesError(e?.message || 'Failed to load sites')
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleSelectSite = (site: Site) => {
    setSelectedSite(site)
    setAssignConfirmOpen(true)
  }

  const deviceDisplayName = useMemo(() => {
    return selectedUnlinked ? (selectedUnlinked.tag || selectedUnlinked.deviceId) : ''
  }, [selectedUnlinked])

  const selectedSiteName = useMemo(() => {
    return selectedSite ? (selectedSite.name || (selectedSite as any).siteName || 'Unknown Site') : ''
  }, [selectedSite])

  const handleConfirmAssign = async () => {
    if (!selectedUnlinked || !selectedSite) {
      setAssignConfirmOpen(false)
      return
    }
    setAssignWorking(true)
    try {
       await assignDeviceToSite(selectedUnlinked.objectId, selectedSite.objectId)
       setAssignConfirmOpen(false)
       setSiteSidebarOpen(false)
       setSelectedSite(null)
       setSelectedUnlinked(null)
       await refreshSites()
       await fetchData()
    } catch (e: any) {
      console.error('Failed to assign device to site:', e)
    } finally {
      setAssignWorking(false)
    }
  }
  // expose fetchData for reuse
  const fetchData = useCallback(async () => {
    if (!selectedDeviceId) return
    dispatch(setLoading(true))
    setError(null)
    try {
      if (userType === 'installer') {
        const [s, inv, unlinked] = await Promise.all([
          getSharingList(selectedDeviceId),
          getPendingInvitation(selectedDeviceId),
          getUnlinkedDeviceList(selectedDeviceId),
        ])
        setSharing(s)
        setInvitations(inv)
        setUnlinkedDevices(unlinked || [])
      } else {
        const [s, inv] = await Promise.all([
          getSharingList(selectedDeviceId),
          getPendingInvitation(selectedDeviceId),
        ])
        setSharing(s)
        setInvitations(inv)
        setUnlinkedDevices([])
      }
    } catch (e: any) {
      console.error('Failed to load sharing/invitations/unlinked devices:', e)
      setError(e?.message || 'Failed to load')
    } finally {
      dispatch(setLoading(false))
    }
  }, [selectedDeviceId, userType, dispatch])

  const handleSendInvite = async (payload: { deviceId: string; email: string; permission: { fullAccess: boolean; readOnly: boolean; ownerShip: boolean } }) => {
    console.log('Send Invitation Payload:', payload)
    setInviteOpen(false)
    await fetchData()
  }

  const handleEditUser = (u: SharedUser) => {
    setEditingUser(u)
    setEditOpen(true)
  }

  const promptRevokeUser = (u: SharedUser) => {
    setConfirmTarget(u)
    setConfirmOpen(true)
  }

  const handleConfirmRevoke = async () => {
    if (!confirmTarget || !selectedDeviceId) {
      setConfirmOpen(false)
      return
    }
    dispatch(setLoading(true))
    setError(null)
    try {
      await apiService.post<{ message: string }>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/revokeInvitation`,
        {
          share_object_id: confirmTarget.objectId,
          device_object_id: selectedDeviceId,
        }
      )
      await fetchData()
      setConfirmOpen(false)
      setConfirmTarget(null)
    } catch (e: any) {
      console.error('Failed to revoke invitation:', e)
      setError(e?.message || 'Failed to revoke invitation')
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleAcceptInvitation = async (inv: Invitation) => {
    try {
      setError(null)
      setAcceptingId(inv.shareObjectId)
      await apiService.post<{ message: string }>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/acceptInvitation`,
        {
          share_object_id: inv.shareObjectId,
        }
      )
      await refreshSites()
      await fetchData()
    } catch (e: any) {
      console.error('Failed to accept invitation:', e)
      setError(e?.message || 'Failed to accept invitation')
    } finally {
      setAcceptingId(null)
    }
  }

  const handleSaveEdit = async (opts: { handOverOwnership: boolean }) => {
    if (!opts?.handOverOwnership) return
    setOwnershipConfirmOpen(true)
  }

  const handleConfirmHandOver = async () => {
    if (!editingUser || !selectedDeviceId) {
      setOwnershipConfirmOpen(false)
      return
    }
    setOwnershipWorking(true)
    setError(null)
    try {
      await apiService.post<{ message: string }>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/changeInvitation`,
        {
          share_object_id: editingUser.objectId,
          new_access: 'owner',
          previous_access: 'full',
        }
      )
      setOwnershipConfirmOpen(false)
      setEditOpen(false)
      setEditingUser(null)
      await fetchData()
    } catch (e: any) {
      console.error('Failed to change invitation:', e)
      setError(e?.message || 'Failed to change invitation')
    } finally {
      setOwnershipWorking(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <div className="space-y-6">
      <Card header="Users & Sharing" headerBorder={true}>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 dark:text-gray-300">Device</span>
              <DeviceSelector
                selectedDeviceId={selectedDeviceId}
                onDeviceChange={(id) => setSelectedDeviceId(id)}
                placeholder="Select a device to view sharing info"
                className="w-[360px]"
              />
            </div>
            {isOwner && selectedDeviceId && (
              <button
                type="button"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                onClick={() => setInviteOpen(true)}
                aria-label="Create invitation"
              >
                <FiPlus className="h-4 w-4" /> Invite
              </button>
            )}
          </div>

        {error && (
          <Alert type="danger">{error}</Alert>
        )}

        {!error && selectedDeviceId && sharing && sharing.hasError && (
          <Alert type="warning">You do not have permission to view sharing info for this device</Alert>
        )}

        { !error && selectedDeviceId && sharing && !sharing.hasError && !hasFullAccess && (
          <Alert type="warning">You need Full Access to view this device's sharing info</Alert>
        )}

        {!error && selectedDeviceId && sharing && !sharing.hasError && hasFullAccess && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <SectionTitle title="Owner" subtitle={`Total ${owners.length}`} />
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {owners.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400">None</div>
                  ) : (
                  owners.map((u) => <UserItem key={`${u.objectId}-${u.inviteEmail}`} user={u} />)
                )}
              </div>
            </div>
            <div>
              <SectionTitle title="Users" subtitle={`Total ${accepted.length}`} />
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {accepted.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400">None</div>
                  ) : (
                  accepted.map((u) => (
                    <UserItem 
                      key={`${u.objectId}-${u.inviteEmail}`} 
                      user={u} 
                      canManage={isOwner}
                      onEdit={handleEditUser}
                      onDelete={promptRevokeUser}
                    />
                  ))
                )}
              </div>
            </div>
            <div>
              <SectionTitle title="Pending" subtitle={`Total ${pending.length}`} />
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {pending.length === 0 ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400">None</div>
                  ) : (
                  pending.map((u) => (
                    <UserItem key={`${u.objectId}-${u.inviteEmail}`} user={u} canManage={isOwner} onDelete={promptRevokeUser} />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </Card>

      <Card header="Invitations" headerBorder={true}>
        <div className="space-y-3">
          {!selectedDeviceId && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Please select a device</div>
          )}
          {selectedDeviceId && invitations.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">No invitations for the selected device</div>
          )}
          {selectedDeviceId && invitations.length > 0 && (
            <div className="space-y-3">
              {invitations.map((inv) => (
                <InvitationItem key={`${inv.shareObjectId}-${inv.email}`} inv={inv} onAccept={handleAcceptInvitation} accepting={acceptingId === inv.shareObjectId} />
              ))}
            </div>
          )}
        </div>
      </Card>

      {userType === 'installer' && (
        <Card header="Unlinked Devices (Installer)" headerBorder={true}>
          {!selectedDeviceId && (
            <div className="text-sm text-gray-500 dark:text-gray-400">Please select a device</div>
          )}
          {selectedDeviceId &&  error && (
            <Alert type="danger">{error}</Alert>
          )}
          {selectedDeviceId && !error && unlinkedDevices.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">No unlinked devices</div>
          )}
          {selectedDeviceId && !error && unlinkedDevices.length > 0 && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {unlinkedDevices.map((d) => (
                <button
                  key={d.objectId}
                  type="button"
                  onClick={() => openSitePickerForDevice(d)}
                  className="w-full text-left py-4 px-4 flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-all group"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate group-hover:text-blue-600">{d.tag || d.deviceId}</div>
                    <div className="text-xs text-gray-500 truncate">{d.address}</div>
                  </div>
                  <FiChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      <InviteUserSidebar
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        deviceId={selectedDeviceId}
        currentUserEmail={currentUser?.email}
        onSendInvite={handleSendInvite}
      />

      <EditUserSidebar
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        currentSharedUser={editingUser}
        onSave={handleSaveEdit}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Revoke Access?"
        onCancel={() => setConfirmOpen(false)}
        onOk={handleConfirmRevoke}
        cancelText="Cancel"
        okText="Confirm"
        disableOk={appLoading}
        loading={appLoading}
      >
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {confirmTarget?.inviteEmail ? (
            <>Do you want to revoke access for <span className="font-medium">{confirmTarget.inviteEmail}</span>?</>
          ) : (
            <>Do you want to revoke this user's access?</>
          )}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={ownershipConfirmOpen}
        title="Hand Over Ownership?"
        onCancel={() => setOwnershipConfirmOpen(false)}
        onOk={handleConfirmHandOver}
        cancelText="Cancel"
        okText="Confirm"
        disableOk={ownershipWorking}
        loading={ownershipWorking}
      >
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {editingUser?.inviteEmail ? (
            <>Do you want to hand over the SplashMe ownership to <span className="font-medium">{editingUser.inviteEmail}</span>?</>
          ) : (
            <>Do you want to hand over the SplashMe ownership?</>
          )}
        </div>
      </ConfirmDialog>

      <SelectSiteSidebar
        isOpen={siteSidebarOpen}
        onClose={() => setSiteSidebarOpen(false)}
        sites={sites}
        error={sitesError}
        onSelectSite={handleSelectSite}
      />

      <ConfirmDialog
        open={assignConfirmOpen}
        title="Assign Device to Site"
        onCancel={() => setAssignConfirmOpen(false)}
        onOk={handleConfirmAssign}
        cancelText="Cancel"
        okText="Confirm"
        disableOk={assignWorking}
        loading={assignWorking}
      >
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {`Do you want to assign device ${deviceDisplayName} to site ${selectedSiteName}`}
        </div>
      </ConfirmDialog
      >
    </div>
  )
}

export default UsersSharingPage